import {
    DEFAULT_MAP_FILTER_STATE,
    type DateRangeOption,
    type LeakVisibilityOption,
    type MapFilterState,
    type ShowOption,
} from './mapFilters';

const MAP_FILTERS_VERSION = 1;
export const MAP_FILTERS_KEY = `orbit.map.filters.v${String(MAP_FILTERS_VERSION)}`;

/**
 * Merges stored boolean-toggle values onto the current defaults.
 * - Known keys present in stored data override the default.
 * - Keys absent from stored data (newly added options) keep their configured default.
 * - Unknown stored keys (removed/renamed options) are ignored.
 * - Non-boolean stored values are ignored.
 */
function mergeBooleanRecord<K extends string>(stored: unknown, defaults: Record<K, boolean>): Record<K, boolean> {
    if (typeof stored !== 'object' || stored === null) return { ...defaults };
    const result = { ...defaults };
    for (const key of Object.keys(defaults) as K[]) {
        const value = (stored as Record<string, unknown>)[key];
        if (typeof value === 'boolean') {
            result[key] = value;
        }
    }
    return result;
}

function validateShowOption(v: unknown): ShowOption {
    if (v === 'all' || v === 'searched' || v === 'created') return v;
    return DEFAULT_MAP_FILTER_STATE.show;
}

function validateDateRangeOption(v: unknown): DateRangeOption {
    if (v === 'all' || v === 'thisMonth' || v === 'thisQuarter' || v === 'thisYear' || v === 'custom') return v;
    return DEFAULT_MAP_FILTER_STATE.dateRange;
}

function validateLeakVisibilityOption(v: unknown): LeakVisibilityOption {
    if (v === 'all' || v === 'visible' || v === 'notVisible') return v;
    return DEFAULT_MAP_FILTER_STATE.leakVisibility;
}

function validateBoolean(v: unknown, fallback: boolean): boolean {
    return typeof v === 'boolean' ? v : fallback;
}

/**
 * Accepts a valid YYYY-MM-DD calendar date string, or returns null.
 * Rejects strings that match the pattern structurally but are impossible dates
 * (e.g. 2026-99-99, 2026-02-31, 2026-13-01) by parsing and round-tripping
 * through the Date constructor.
 *
 * Uses UTC construction + setUTCFullYear to avoid the JS quirk where
 * new Date(year, ...) with year 0–99 is treated as 1900 + year.
 * Accepts any structurally valid 4-digit calendar year, including 0000–0099.
 * No date library dependency needed.
 */
function validateIsoDateOrNull(v: unknown): string | null {
    if (v === null || v === undefined) return null;
    if (typeof v !== 'string') return null;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return null;
    const [yearPart, monthPart, dayPart] = v.split('-');
    const year = Number(yearPart);
    const month = Number(monthPart); // 1-indexed
    const day = Number(dayPart);
    const date = new Date(Date.UTC(0, month - 1, day));
    date.setUTCFullYear(year);
    if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
        return null;
    }
    return v;
}

function validateCustomDateRange(v: unknown): { from: string | null; to: string | null } {
    if (typeof v !== 'object' || v === null) {
        return { from: null, to: null };
    }
    const obj = v as Record<string, unknown>;
    return {
        from: validateIsoDateOrNull(obj['from']),
        to: validateIsoDateOrNull(obj['to']),
    };
}

/**
 * Validates and repairs any untrusted MapFilterState-shaped input — localStorage,
 * URL params, or future sources. Missing/invalid fields fall back to defaults.
 * Extra unknown keys are dropped. Never throws.
 */
export function validateMapFilterState(stored: unknown): MapFilterState {
    if (typeof stored !== 'object' || stored === null) {
        return { ...DEFAULT_MAP_FILTER_STATE };
    }
    const obj = stored as Record<string, unknown>;
    return {
        show: validateShowOption(obj['show']),
        dateRange: validateDateRangeOption(obj['dateRange']),
        customDateRange: validateCustomDateRange(obj['customDateRange']),
        leakVisibility: validateLeakVisibilityOption(obj['leakVisibility']),
        noDigRepairs: validateBoolean(obj['noDigRepairs'], DEFAULT_MAP_FILTER_STATE.noDigRepairs),
        leakTypes: mergeBooleanRecord(obj['leakTypes'], DEFAULT_MAP_FILTER_STATE.leakTypes),
        pipeTypes: mergeBooleanRecord(obj['pipeTypes'], DEFAULT_MAP_FILTER_STATE.pipeTypes),
    };
}

/**
 * Loads active filter state from localStorage.
 *
 * Returns null when:
 *   - nothing is stored
 *   - JSON is malformed
 *   - version is not 1
 *   - isActive is missing or not a boolean
 *   - isActive is false (nothing meaningful to restore; caller uses defaults)
 *
 * When isActive is true, partial committed values are repaired against
 * DEFAULT_MAP_FILTER_STATE rather than causing a null return, so newly-added
 * filter keys (e.g. pipeTypes.fitting added after the user last saved) always
 * receive a valid default.
 */
export function loadPersistedMapFilters(): {
    isActive: boolean;
    committed: MapFilterState;
} | null {
    try {
        const raw = localStorage.getItem(MAP_FILTERS_KEY);
        if (!raw) return null;

        const parsed: unknown = JSON.parse(raw);
        if (typeof parsed !== 'object' || parsed === null) return null;

        const obj = parsed as Record<string, unknown>;
        if (obj['version'] !== MAP_FILTERS_VERSION) return null;

        const isActive = obj['isActive'];
        if (typeof isActive !== 'boolean') return null;
        if (!isActive) return null; // inactive state — caller uses defaults

        return {
            isActive: true,
            committed: validateMapFilterState(obj['committed']),
        };
    } catch {
        return null;
    }
}

/** Persists active filter state to localStorage. Silently ignores write failures. */
export function savePersistedMapFilters(state: { isActive: boolean; committed: MapFilterState }): void {
    try {
        localStorage.setItem(MAP_FILTERS_KEY, JSON.stringify({ version: MAP_FILTERS_VERSION, ...state }));
    } catch {
        // localStorage may be unavailable (private browsing, storage quota, etc.)
    }
}

/** Removes the persisted filter state entry from localStorage. Silently ignores failures. */
export function clearPersistedMapFilters(): void {
    try {
        localStorage.removeItem(MAP_FILTERS_KEY);
    } catch {
        // localStorage may be unavailable
    }
}
